import updateServiceWorkerCache from 'serviceWorkers/cacheUpdate';
import {ActivitiesByStatus, Activity} from './types';

export const updateActivitiesCache =
  updateServiceWorkerCache<ActivitiesByStatus>({
    cacheName: 'letrusAPI-userActivities', // service worker cache group name
    endpoint: `${process.env.REACT_APP_API_URL}/api/v1/activities/`, // cached endpoint url
  });

// utilitary functions
export const cachedActivity = {
  updateStartDate: (targetId: number) =>
    updateActivitiesCache((activitiesByStatus) =>
      updateActivityParam(activitiesByStatus, targetId, [
        'started',
        new Date().toISOString(),
      ]),
    ),
  updateFocusModeStart: (
    targetId: number,
    started: string,
    completionDeadline: string,
  ) =>
    updateActivitiesCache((activitiesByStatus) =>
      updateActivityParams(activitiesByStatus, targetId, {
        started,
        completion_deadline: completionDeadline,
      }),
    ),
  updateStepNumber: (targetId: number, step_number: number) =>
    updateActivitiesCache((activitiesByStatus) =>
      updateActivityParam(activitiesByStatus, targetId, [
        'step_number',
        step_number,
      ]),
    ),
  updateSteps: (targetId: number, steps?: Activity['steps']) =>
    updateActivitiesCache((activitiesByStatus) =>
      updateActivityParam(activitiesByStatus, targetId, ['steps', steps]),
    ),
  updateFinishDate: (activityId?: number) => {
    return updateActivitiesCache((activitiesByStatus) => {
      if (!activityId) return activitiesByStatus;
      const activityToMove =
        findActivity(activitiesByStatus.opened, activityId) ||
        findActivity(activitiesByStatus.in_progress, activityId);

      if (!activityToMove) return activitiesByStatus;

      return {
        ...activitiesByStatus,
        opened: filterActivity(activitiesByStatus.opened, activityId),
        in_progress: filterActivity(activitiesByStatus.in_progress, activityId),
        finished: [
          ...activitiesByStatus.finished,
          {...activityToMove, status: 'finished'},
        ],
      };
    });
  },
};

// helper functions
function updateActivityParam(
  activitiesByStatus: ActivitiesByStatus,
  activityId: number,
  param: [string, unknown],
) {
  return Object.entries(activitiesByStatus).reduce(
    (acc, [status, activities]) => ({
      ...acc,
      [status]: activities.map((activity) =>
        activity.id === activityId
          ? {...activity, [param[0]]: param[1]}
          : activity,
      ),
    }),
    {},
  ) as ActivitiesByStatus;
}

function updateActivityParams(
  activitiesByStatus: ActivitiesByStatus,
  activityId: number,
  params: Partial<Activity>,
) {
  return Object.entries(activitiesByStatus).reduce(
    (acc, [status, activities]) => ({
      ...acc,
      [status]: activities.map((activity) =>
        activity.id === activityId ? {...activity, ...params} : activity,
      ),
    }),
    {},
  ) as ActivitiesByStatus;
}

function filterActivity(activityList: Activity[], activityId: number) {
  return activityList.filter((activity) => activity.id !== activityId) || [];
}

function findActivity(activityList: Activity[], activityId: number) {
  return activityList.find((activity) => activity.id === activityId);
}
